import { Combobox, Label, Option, OptionGroup, useId } from '@fluentui/react-components';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEnterAmountsDialogComboboxData } from '../../hooks/useEnterAmountsDialogComboboxData';
import { useEnterAmountsDialogStyles } from '../../hooks/useEnterAmountsDialogStyles';
import { useHiddenComboboxCheckIconStyles } from '../../hooks/useHiddenComboboxCheckIconStyles';
import { IJurisdiction } from '../../model';
import {
    EnterAmountsDialogFiltersActionType,
    IEnterAmountsDialogComboboxPropsBase,
    IEnterAmountsDialogFilterJurisdictionChangeAction,
} from '../../reducers/enterAmountsDialogFiltersReducer';
import { useVirtualizedCombobox, VirtualizedOptionList } from '../common/VirtualizedOptionList';

interface IJurisdictionComboboxOptionProps {
    jurisdiction: IJurisdiction;
    optionToSelectedOption: (jurisdiction: IJurisdiction) => string;
    optionToInputValue: (jurisdiction: IJurisdiction) => string;
}
const JurisdictionComboboxOption: FC<IJurisdictionComboboxOptionProps> = (props) => {
    const { jurisdiction, optionToSelectedOption, optionToInputValue } = props;
    const { hidden: hiddenCheckBoxCssClass } = useHiddenComboboxCheckIconStyles();
    return (
        <Option
            key={optionToSelectedOption(jurisdiction)}
            className={hiddenCheckBoxCssClass}
            value={optionToSelectedOption(jurisdiction)}
            text={optionToInputValue(jurisdiction)}
            aria-label={optionToInputValue(jurisdiction)}
        >
            {optionToInputValue(jurisdiction)}
        </Option>
    );
};

export interface IEnterAmountsDialogJurisdictionComboboxProps
    extends IEnterAmountsDialogComboboxPropsBase<IJurisdiction, IEnterAmountsDialogFilterJurisdictionChangeAction> {}

export const JurisdictionCombobox: FC<IEnterAmountsDialogJurisdictionComboboxProps> = (
    props: IEnterAmountsDialogJurisdictionComboboxProps
) => {
    const jurisdictionLabelId: string = useId('jurisdiction');
    const { comboboxStyles } = useEnterAmountsDialogStyles();
    const { t } = useTranslation();
    const optionToSelectedOption: (option: IJurisdiction) => string = (option: IJurisdiction) => option.code!;
    const optionToInputValue: (option: IJurisdiction) => string = (option: IJurisdiction) => `${option.code} - ${option.name}`;
    const findOptionsByDisplayValue: (displayValue: string, options: IJurisdiction[]) => IJurisdiction[] = (
        displayValue: string,
        options: IJurisdiction[]
    ) =>
        options.filter((jurisdiction: IJurisdiction) =>
            `${jurisdiction.code} - ${jurisdiction.name}`.toLowerCase().includes(displayValue.toLowerCase())
        );
    const findOptionByKey: (key: string | undefined, options: IJurisdiction[]) => IJurisdiction | undefined = (
        key: string | undefined,
        options: IJurisdiction[]
    ) => options.find((jurisdiction: IJurisdiction) => jurisdiction.code === key);
    const optionRenderer: (jurisdiction: IJurisdiction) => JSX.Element = useCallback(
        (jurisdiction: IJurisdiction) => (
            <JurisdictionComboboxOption
                key={optionToSelectedOption(jurisdiction)}
                jurisdiction={jurisdiction}
                optionToSelectedOption={optionToSelectedOption}
                optionToInputValue={optionToInputValue}
            />
        ),
        [optionToSelectedOption, optionToInputValue]
    );
    const {
        inputValue,
        selectedOptions,
        matchingOptionsData,
        searchText,
        onOptionSelect: handleDataOptionSelect,
        onBlur,
        onChange,
        disabled,
        onKeyDown,
        comboboxRef,
    } = useEnterAmountsDialogComboboxData<
        IJurisdiction,
        IEnterAmountsDialogFilterJurisdictionChangeAction,
        IEnterAmountsDialogJurisdictionComboboxProps
    >(
        props,
        {
            optionToInputValue,
            optionToSelectedOption,
            findOptionsByDisplayValue,
            findOptionByKey,
        },
        EnterAmountsDialogFiltersActionType.JurisdictionChanged
    );

    const virtualizerConfig = useVirtualizedCombobox({
        selectedOptions,
        matchingOptionsData,
        optionToSelectedOption,
        onOptionSelect: handleDataOptionSelect,
    });
    const {
        scrollRef,
        listboxClassName,
        onOptionSelect: handleVirtualizedOptionSelect,
        onOpenChange: handleVirtualizedOpenChange,
    } = virtualizerConfig;
    return (
        <div>
            <Label id={jurisdictionLabelId} weight='semibold'>
                {t('jurisdiction')}
            </Label>
            <Combobox
                className={comboboxStyles}
                aria-labelledby={jurisdictionLabelId}
                value={inputValue}
                selectedOptions={selectedOptions}
                onOptionSelect={handleVirtualizedOptionSelect}
                onOpenChange={handleVirtualizedOpenChange}
                disabled={disabled}
                freeform
                onChange={onChange}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                ref={comboboxRef}
                clearable={props.clearable}
                listbox={{
                    ref: scrollRef,
                    className: listboxClassName,
                }}
            >
                {searchText ? (
                    <Option aria-disabled='true' key='freeform' text={searchText} disabled={true}>
                        {t('noOptionsFound', { searchText: searchText })}
                    </Option>
                ) : (
                    <OptionGroup>
                        <VirtualizedOptionList
                            items={matchingOptionsData}
                            renderOption={optionRenderer}
                            virtualizerConfig={virtualizerConfig}
                        />
                    </OptionGroup>
                )}
            </Combobox>
        </div>
    );
};
